/**
 * Standalone conversation identity (session-hardening S3, Miles 2026-07-12).
 *
 * Surfaces WITHOUT the thread switcher used to fall back to the literal
 * thread id `'default'` — funnelling every conversation on every such page,
 * forever, into ONE shared server session (`sess_thr_default`) whose recent
 * transcript replayed across what the user perceived as separate
 * conversations. That was a real cross-conversation dialog bleed.
 *
 * Instead, each account gets a minted, localStorage-persisted conversation id
 * that plays the role of a thread id for the single standalone conversation.
 * "Clear conversation" ROTATES it — the fresh id derives a fresh server
 * session, so a cleared chat can never be haunted by the old transcript.
 * Threading surfaces never touch this module (they always have real ids).
 */

const STORAGE_PREFIX = 'prest0_standalone_conv_';

/** In-memory fallback when localStorage is unavailable (SSR, privacy modes). */
const memoryFallback = new Map<string, string>();

function mint(): string {
  const ts = Date.now().toString(36);
  let rand = '';
  try {
    const bytes = new Uint8Array(6);
    crypto.getRandomValues(bytes);
    rand = Array.from(bytes, (b) => (b % 36).toString(36)).join('');
  } catch {
    rand = Math.random().toString(36).slice(2, 8);
  }
  return `conv_${ts}_${rand}`;
}

function storageKey(accountId: string): string {
  return `${STORAGE_PREFIX}${accountId}`;
}

/** The account's current standalone conversation id (minted + persisted on first use). */
export function getOrMintStandaloneConversationId(accountId: string): string {
  const key = storageKey(accountId);
  try {
    const existing = localStorage.getItem(key);
    if (existing && existing.trim()) return existing.trim();
  } catch { /* fall through to memory */ }
  const fromMemory = memoryFallback.get(key);
  if (fromMemory) return fromMemory;
  const minted = mint();
  try {
    localStorage.setItem(key, minted);
  } catch { /* memory only */ }
  memoryFallback.set(key, minted);
  return minted;
}

/**
 * Rotate to a fresh conversation id (the "clear conversation" contract):
 * subsequent sends derive a brand-new server session.
 */
export function rotateStandaloneConversationId(accountId: string): string {
  const key = storageKey(accountId);
  const minted = mint();
  try {
    localStorage.setItem(key, minted);
  } catch { /* memory only */ }
  memoryFallback.set(key, minted);
  return minted;
}

export function __resetStandaloneConversationIdsForTests(): void {
  memoryFallback.clear();
}
